import { A, useNavigate } from "@solidjs/router";
import type { PromptBundle } from "contracts/api/prompts/responses";
import { createSignal, For, Show } from "solid-js";
import { isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { errorMessage } from "../../store/api-client";
import { relativeAge } from "../../ui/format";
import {
  createPromptBundles,
  invalidatePromptBundles,
} from "./playgroundQueries";

/** Library of yeet-agent prompts. The starred one is what new runs use by
 * default; any prompt can be activated, and editing forks a new version. */
export function PromptBundleList() {
  const navigate = useNavigate();
  const bundles = createPromptBundles();
  const [error, setError] = createSignal("");

  const activate = async (bundle: PromptBundle) => {
    setError("");
    try {
      await api.prompts.activateBundle(bundle.id);
      await invalidatePromptBundles();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href="/playground" class="landing-brand">
          ← Playground
        </A>
        <span class="landing-tag">Prompt library</span>
      </div>
      <div class="landing-main">
        <div class="landing-head-row">
          <div>
            <h1 class="landing-h1">AI instructions</h1>
            <p class="landing-sub pg-lede">
              These are the instructions the agent follows when it audits a
              page. The starred prompt is used by default; edit one to try a
              wording change (each edit saves a new version, so the original is
              kept).
            </p>
          </div>
          <A href="/playground/prompts/new" class="btn btn-primary">
            New prompt
          </A>
        </div>
        <Show when={error()}>
          <div class="gate-error">{error()}</div>
        </Show>
        <div class="panel">
          <Show
            when={bundles.data}
            fallback={
              <div class="loading" style={{ padding: "16px" }}>
                Loading…
              </div>
            }
          >
            {(data) => (
              <table class="tbl tbl-link">
                <thead>
                  <tr>
                    <th>Prompt</th>
                    <th>State</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={data().bundles}>
                    {(bundle) => (
                      <tr
                        class="tbl-row"
                        onClick={() =>
                          navigate(`/playground/prompts/${bundle.id}`)
                        }
                      >
                        <td>{bundle.label}</td>
                        <td>
                          <Show when={bundle.isActive}>
                            <span class="badge badge-status st-done">
                              ★ default
                            </span>{" "}
                          </Show>
                          <Show when={bundle.referenced}>
                            <span class="badge">used by runs</span>
                          </Show>
                        </td>
                        <td>{relativeAge(bundle.createdAt)}</td>
                        <td>
                          <Show when={isAdmin() && !bundle.isActive}>
                            <button
                              type="button"
                              class="btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                void activate(bundle);
                              }}
                            >
                              Make default
                            </button>
                          </Show>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            )}
          </Show>
        </div>
      </div>
    </main>
  );
}
